"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * @module CryptoUtils
 */
var utils_1 = require("./model/utils");
var helpers_1 = require("./helpers");
var cryptoJs = require("crypto-js");
var validator_1 = require("./modules/validator");
var types_1 = require("./model/types");
var RIPEMD160 = require('ripemd160');
var CryptoUtilsError;
(function (CryptoUtilsError) {
    CryptoUtilsError["invalid_parameters"] = "invalid_parameters";
})(CryptoUtilsError = exports.CryptoUtilsError || (exports.CryptoUtilsError = {}));
/**
 * Custom class for CryptoJS encryption serialization.
 */
var CryptoJSAesJson = /** @class */ (function () {
    function CryptoJSAesJson() {
    }
    CryptoJSAesJson.prototype.stringify = function (cipherParams) {
        var j = { ct: cipherParams.ciphertext.toString(cryptoJs.enc.Base64) };
        if (cipherParams.iv) {
            j['iv'] = cipherParams.iv.toString();
        }
        if (cipherParams.salt) {
            j['s'] = cipherParams.salt.toString();
        }
        return JSON.stringify(j);
    };
    CryptoJSAesJson.prototype.parse = function (jsonStr) {
        var j = JSON.parse(jsonStr);
        var cipherParams = { ciphertext: cryptoJs.enc.Base64.parse(j.ct), iv: '', salt: '' };
        if (j.iv) {
            cipherParams.iv = cryptoJs.enc.Hex.parse(j.iv);
        }
        if (j.s) {
            cipherParams.salt = cryptoJs.enc.Hex.parse(j.s);
        }
        return cipherParams;
    };
    return CryptoJSAesJson;
}());
exports.CryptoJSAesJson = CryptoJSAesJson;
var CryptoUtils = /** @class */ (function () {
    function CryptoUtils() {
    }
    /**
     * Encrypts message with given private-pubic key pair
     *
     * @param {string} message          Message to encrypt.
     * @param {string} privateKey       Private of one side of communication, to encrypt message with.
     * @param {string} publicKey        Public key of other side of communication, used in encryption.
     * @param {string} [nonce]          Random number user in encryption process. Default ''.
     * @return {string}                 String in HEX format with encrypted message.
     */
    CryptoUtils.encryptWithChecksum = function (message, privateKey, publicKey, nonce) {
        if (nonce === void 0) { nonce = ''; }
        if (!validator_1.Validator.validateArguments([message, privateKey, publicKey, nonce], [types_1.Type.string, types_1.Type.string, types_1.Type.string, types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return helpers_1.dcorejs_lib.Aes.encrypt_with_checksum(utils_1.KeyPrivate.fromWif(privateKey).key, utils_1.KeyPublic.fromString(publicKey).key, nonce, message).toString('hex');
    };
    /**
     * Decrypts message encrypted by CryptoUtils.encryptWithChecksum or memo from DCore network objects.
     *
     * @param {string} message          Encrypted message.
     * @param {KeyPrivate} privateKey   Private key of one of communicating sides. Used to decrypt message.
     * @param {KeyPublic} publicKey     Public key of other communicating side. Used to decrypt message.
     * @param {string} nonce            Random number used in decryption. Default ''.
     * @returns {Buffer}                Buffer with decrypted text.
     */
    CryptoUtils.decryptWithChecksum = function (message, privateKey, publicKey, nonce) {
        if (nonce === void 0) { nonce = ''; }
        if (!validator_1.Validator.validateArguments([message, privateKey, publicKey, nonce], [types_1.Type.string, types_1.Type.string, types_1.Type.string, types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return helpers_1.dcorejs_lib.Aes.decrypt_with_checksum(utils_1.KeyPrivate.fromWif(privateKey).key, utils_1.KeyPublic.fromString(publicKey).key, nonce, message).toString('utf8');
    };
    /**
     * Calculate RIPEMD160 hash from input.
     * Used as 'hash' parameter when submitting content.
     *
     * @param {string} fromBuffer   Input to calculate buffer from.
     * @returns {string}            RIPEMD160 hashed text.
     */
    CryptoUtils.ripemdHash = function (fromBuffer) {
        if (fromBuffer === undefined) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return new RIPEMD160().update(fromBuffer).digest('hex');
    };
    /**
     * Calculates MD5 hash out of input.
     *
     * @param {string} message  Input message.
     * @returns {string}        Hashed input.
     */
    CryptoUtils.md5 = function (message) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return cryptoJs.MD5(message).toString();
    };
    /**
     * Calculates SHA512 hash of input.
     *
     * @param message       Input message.
     * @returns {string}    Hashed input.
     */
    CryptoUtils.sha512 = function (message) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return cryptoJs.SHA512(message).toString(cryptoJs.enc.Hex);
    };
    /**
     * Calculates SHA256 hash of input.
     *
     * @param message       Input message.
     * @returns {string}    Hashed input.
     */
    CryptoUtils.sha256 = function (message) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return cryptoJs.SHA256(message).toString(cryptoJs.enc.Hex);
    };
    /**
     * Encrypt message using AES256-CBC.
     * Encrypted text is in string representation of following type
     * {
     *  ct: string,
     *  iv: string,
     *  s: string
     * }
     * @param {string} message      Message to be encrypted.
     * @param {string} password     Password for encryption.
     * @returns {string}            Encrypted serialized message.
     */
    CryptoUtils.encrypt = function (message, password) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string, types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return cryptoJs.AES.encrypt(message, password, { format: CryptoJSAesJson.prototype }).toString();
    };
    /**
     * Decrypt AES256-CBC encrypted message in form of string representation of following type
     * {
     *  ct: string,
     *  iv: string,
     *  s: string
     * }
     * @param {string} message      Serialized encrypted message to be decrypted.
     * @param {string} password     Password for decryption.
     * @returns {string | null}     Decrypted message.
     */
    CryptoUtils.decrypt = function (message, password) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string, types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        return cryptoJs.AES.decrypt(message, password, { format: CryptoJSAesJson.prototype }).toString(cryptoJs.enc.Utf8) || null;
    };
    /**
     * Encrypt message with AES256-CBC. Result is encrypted hex string.
     * This encryption is compatible with wallet-cli wallet file export key encryption format.
     *
     * @param {string | Buffer} message     Message to be encrypted.
     * @param {string} password             Password for encryption.
     * @returns {string}                    Encrypted message.
     */
    CryptoUtils.encryptToHexString = function (message, password) {
        if (message === undefined || password === undefined || !validator_1.Validator.validateArguments([password], [types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        var hash = CryptoUtils.sha512(password);
        var ivHex = hash.substr(64, 32);
        var keyHex = hash.substr(0, 64);
        var iv = cryptoJs.enc.Hex.parse(ivHex);
        var key = cryptoJs.enc.Hex.parse(keyHex);
        var msg = typeof message === 'string' ? Buffer.from(message, 'binary') : message;
        var plainArr = cryptoJs.enc.Hex.parse(msg.toString('hex'));
        var res = cryptoJs.AES.encrypt(plainArr, key, { iv: iv });
        return cryptoJs.enc.Hex.stringify(res.ciphertext);
    };
    /**
     * Decrypts AES256-CBC encrypted hex string message. Result is decrypted string.
     * This decryption is compatible with wallet-cli wallet file export key encryption format.
     *
     * @param {string} message          Message to be decrypted.
     * @param {string} password         Password for decryption.
     * @returns {string}                Decrypted message.
     */
    CryptoUtils.decryptHexString = function (message, password) {
        if (!validator_1.Validator.validateArguments(arguments, [types_1.Type.string, types_1.Type.string])) {
            throw new TypeError(CryptoUtilsError.invalid_parameters);
        }
        var hash = CryptoUtils.sha512(password);
        var ivHex = hash.substr(64, 32);
        var keyHex = hash.substr(0, 64);
        var iv = cryptoJs.enc.Hex.parse(ivHex);
        var key = cryptoJs.enc.Hex.parse(keyHex);
        var cipher_array = cryptoJs.enc.Hex.parse(message);
        var plainwords = cryptoJs.AES.decrypt({ ciphertext: cipher_array, salt: null, iv: iv }, key, { iv: iv });
        var plainHex = cryptoJs.enc.Hex.stringify(plainwords);
        var buff = Buffer.from(plainHex, 'hex');
        return buff.toString();
    };
    return CryptoUtils;
}());
exports.CryptoUtils = CryptoUtils;
//# sourceMappingURL=crypt.js.map


//////////////////
// WEBPACK FOOTER
// ./lib/crypt.js
// module id = 26
// module chunks = 0